"use client"

import React, { useState } from "react"
import { ArrowLeft, ArrowUpRight, Check, Share2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { PublicNavGlass, type GlassNavMode } from "@/components/public-nav-glass"
import { PublicFooter } from "@/components/public-footer"
import { LocaleLink } from "@/components/locale-link"
import { GlassSurface, Reveal, displayHeadingStyle } from "@/components/glass/glass-kit"
import { HelpPlate } from "@/components/help/help-plates"
import { BlogAuthorByline } from "@/components/blog/blog-author-byline"
import { Receipt, StackedBars } from "@/components/blog/article-blocks"
import { JournalCategoryLabel } from "@/components/journal-category-label"
import { BLOG_AUTHOR } from "@/lib/blog-authors"
import type { BlogArticleBody } from "@/lib/blog-article-bodies"
import type { BlogArticle } from "@/lib/blog-articles"
import { getBlogCategoryLabel } from "@/lib/blog-articles"
import { labelInk } from "@/lib/journal-accent"
import { Lozenge } from "@/components/ui/lozenge"

const TITLE = "clamp(1.9rem, 4.4vw, 3.1rem)"
const SECTION_TITLE = "clamp(1.35rem, 2.3vw, 1.7rem)"

function articleDisplayDate(publishDate: string) {
  return publishDate.split("|")[0]?.trim() ?? publishDate
}

/**
 * What body copy may contain: **bold**, [markdown links](https://…), and bare
 * URLs on our own domain.
 *
 * Article bodies are plain strings. Bold came first, because a founder piece
 * leans on a bolded line the way a spoken pitch leans on a pause. Links came
 * because a blog that cannot link is not really a blog — the launch post ended
 * on "Try your city at boothapp.co/search" and there was nothing to click.
 *
 * Bold is parsed first, then links inside each run, then bare URLs in whatever
 * text is left. A link's own label can be bold; a bold run can contain a link.
 */
function withEmphasis(text: string): React.ReactNode {
  if (!text.includes("**")) return withLinks(text)
  return text.split("**").map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-medium" style={{ color: "var(--pg-fg)" }}>
        {withLinks(part)}
      </strong>
    ) : (
      <React.Fragment key={i}>{withLinks(part)}</React.Fragment>
    ),
  )
}

/** [label](href) — the label is what shows, the href is where it goes.
    href is a web URL, a path on this site, or a mailto: address. */
const MD_LINK = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*|mailto:[^\s)]+)\)/g

/**
 * Bare URLs, our domain only. Writing boothapp.co/search in a sentence should
 * link without ceremony. Everything else needs the markdown form: auto
 * linkifying any URL-shaped text would turn a sentence mentioning a competitor
 * into an outbound link, which is not a regex's decision to make. Trailing
 * sentence punctuation stays outside the anchor.
 */
// Not after "@" or a word character: in partnerships@boothapp.co the domain
// is part of an email address, and linking it to the website would send
// someone who meant to write to us to the home page instead.
const BOOTH_URL = /((?<![@\w.])(?:https?:\/\/)?(?:www\.)?boothapp\.co(?:\/[^\s,)]*[^\s,).!?])?)/g

const LINK_CLASS = "underline underline-offset-2 transition-opacity hover:opacity-70"
const LINK_STYLE: React.CSSProperties = { color: "var(--pg-fg)", textDecorationThickness: 1 }

/** Off-site links open in a new tab and carry rel; ours stay in this one. */
function Anchor({ href, children }: { href: string; children: React.ReactNode }) {
  const external = /^https?:\/\//.test(href) && !/(^|\.)boothapp\.co/.test(new URL(href).hostname)
  return (
    <a
      href={href}
      className={LINK_CLASS}
      style={LINK_STYLE}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  )
}

function withLinks(text: string): React.ReactNode {
  if (!text.includes("[") && !text.includes("boothapp.co")) return text
  const out: React.ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  MD_LINK.lastIndex = 0
  while ((m = MD_LINK.exec(text)) !== null) {
    if (m.index > last) out.push(<React.Fragment key={`t${last}`}>{bareUrls(text.slice(last, m.index))}</React.Fragment>)
    out.push(
      <Anchor key={`l${m.index}`} href={m[2]}>
        {m[1]}
      </Anchor>,
    )
    last = m.index + m[0].length
  }
  if (last === 0) return bareUrls(text)
  if (last < text.length) out.push(<React.Fragment key={`t${last}`}>{bareUrls(text.slice(last))}</React.Fragment>)
  return out
}

function bareUrls(text: string): React.ReactNode {
  if (!text.includes("boothapp.co")) return text
  return text.split(BOOTH_URL).map((part, i) => {
    if (i % 2 === 0) return part
    const href = part.startsWith("http") ? part : `https://${part.startsWith("www.") ? "" : "www."}${part}`
    return (
      <Anchor key={i} href={href}>
        {part}
      </Anchor>
    )
  })
}

export default function BlogArticleClient({
  article,
  meta,
}: {
  article: BlogArticleBody
  meta: BlogArticle
}) {
  const t = useTranslations("pages.articles")
  const [mode, setMode] = useState<GlassNavMode>("dark")
  const [copied, setCopied] = useState(false)
  const accent = `rgb(${meta.accent})`

  const handleShare = async () => {
    const url = window.location.href
    const title = article.title

    if (navigator.share) {
      try {
        await navigator.share({ title, url })
        return
      } catch {
        // fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  return (
    <GlassSurface mode={mode}>
      <PublicNavGlass mode={mode} onModeChange={setMode} />

      <main className="mx-auto w-full max-w-6xl min-w-0 px-5 pb-24 pt-28 sm:px-8 sm:pt-36">
        <header className="min-w-0">
          <Reveal staticBelowMd>
            <LocaleLink
              href="/blog"
              className="inline-flex items-center gap-2 text-[12.5px] transition-opacity hover:opacity-60"
              style={{ color: "var(--pg-muted)" }}
            >
              <ArrowLeft className="h-3 w-3" strokeWidth={1.75} />
              {t("back")}
            </LocaleLink>
          </Reveal>

          <Reveal delay={60} staticBelowMd>
            <div
              className="mt-8 flex items-center justify-between gap-4 pb-5 text-[12.5px]"
              style={{ color: "var(--pg-muted)", borderBottom: "1px solid var(--pg-line)" }}
            >
              {/* One label: the category, led by its lozenge. The publication
                  name read as a second, competing tag beside it. */}
              <span className="inline-flex min-w-0 items-center gap-1">
                <Lozenge color={accent} />
                <JournalCategoryLabel
                  label={getBlogCategoryLabel(meta)}
                  style={{ color: labelInk(meta) }}
                />
              </span>

              {/* The icon is sized in whole pixels with a fixed 1.5px stroke. At
                  16px the default stroke scaled to ~1.17px, which iOS drew
                  soft and jagged on the little share circles. */}
              <button
                type="button"
                onClick={handleShare}
                aria-label={copied ? t("copied") : t("share")}
                className="-m-2 inline-flex shrink-0 items-center gap-1.5 p-2 text-[12.5px] font-medium transition-opacity duration-200 hover:opacity-55"
                style={{ color: "var(--pg-fg)" }}
              >
                {copied ? (
                  <>
                    <Check size={18} strokeWidth={1.5} absoluteStrokeWidth shapeRendering="geometricPrecision" className="text-emerald-400" />
                    <span>{t("copied")}</span>
                  </>
                ) : (
                  <>
                    <Share2 size={18} strokeWidth={1.5} absoluteStrokeWidth shapeRendering="geometricPrecision" />
                    <span className="hidden sm:inline">{t("share")}</span>
                  </>
                )}
              </button>
            </div>

            {/* Phones lead with the picture and put the title under it; from md
                up the title sits above the hero as before. Order, not a second
                copy of either, so there is one h1 on the page. */}
            <div className="flex flex-col">
              <h1
                className="mt-6 min-w-0 max-w-full font-medium break-words md:mt-9"
                style={displayHeadingStyle(TITLE, "-0.035em")}
              >
                {article.title}
              </h1>

              {article.image ? (
                <div
                  className="order-first mt-8 w-full overflow-hidden rounded-[22px] md:order-none md:mt-10"
                  style={{ border: "1px solid var(--pg-card-edge)" }}
                >
                  <img src={article.image} alt={article.title} className="h-auto w-full object-cover" />
                </div>
              ) : (
                <HelpPlate
                  kind={meta.plate}
                  figure={1}
                  caption={meta.plateCaption}
                  accent={meta.accent}
                  live
                  ratio="21 / 9"
                  mobileRatio="16 / 9"
                  className="order-first mt-8 w-full md:order-none md:mt-10"
                />
              )}
            </div>
          </Reveal>
        </header>

        {/* On desktop the body column sits on the RIGHT of the page, 6.66rem
            in from the edge — the hero plate above is full width, and a text
            column pinned to the left under it left the whole right half
            empty. Sizes inline: arbitrary Tailwind values (max-w-[42rem],
            lg:mr-[6.66rem]) silently drop out of this build. Below lg the
            column stays where it was. */}
        <article className="article-body mt-8 min-w-0 md:mt-20">
          <Reveal staticBelowMd>
            <BlogAuthorByline
              tone="glass"
              date={articleDisplayDate(article.publishDate)}
              authorKey={meta.author}
              showAvatar
              className="mb-4"
            />
            {/* whitespace-pre-line, same as the sections below: an intro written
                as short lines (the founder pieces) was collapsing into one run. */}
            <p className="max-w-2xl text-[15px] leading-[1.75] whitespace-pre-line" style={{ color: "var(--pg-fg)" }}>
              {withEmphasis(article.content)}
            </p>
          </Reveal>

          {article.sections.map((section, index) => (
            <Reveal key={section.heading || `section-${index}`} delay={index * 40} staticBelowMd>
              <section className="pt-10 sm:pt-14">
                {section.heading ? (
                  <h2
                    className="min-w-0 max-w-full font-semibold break-words"
                    style={{
                      fontSize: SECTION_TITLE,
                      lineHeight: 1.25,
                      letterSpacing: "-0.025em",
                      color: "var(--pg-fg)",
                    }}
                  >
                    {section.heading}
                  </h2>
                ) : null}

                {section.content ? (
                  <p
                    className={`max-w-2xl text-[16px] leading-[1.7] whitespace-pre-line ${section.heading ? "mt-4" : ""}`}
                    style={{ color: "var(--pg-fg)" }}
                  >
                    {withEmphasis(section.content)}
                  </p>
                ) : null}

                {section.receipt ? <Receipt receipt={section.receipt} /> : null}
                {section.chart ? <StackedBars chart={section.chart} mode={mode} /> : null}

                {section.after ? (
                  <p className="mt-8 max-w-2xl text-[16px] leading-[1.7] whitespace-pre-line" style={{ color: "var(--pg-fg)" }}>
                    {withEmphasis(section.after)}
                  </p>
                ) : null}

                {section.quote ? (
                  <blockquote
                    className="relative mt-6 max-w-2xl rounded-[18px] px-5 py-4"
                    style={{
                      background: "var(--pg-card)",
                      border: "1px solid var(--pg-card-edge)",
                    }}
                  >
                    {/* The accent is a thin straight rule inside the card, held
                        clear of the rounded corners. A thick left border on a
                        rounded box bent into a half-moon at top and bottom. */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute left-0 top-5 bottom-5 w-0.5 rounded-full"
                      style={{ background: accent }}
                    />
                    <h3
                      className="min-w-0 font-medium break-words"
                      style={{ fontSize: SECTION_TITLE, lineHeight: 1.3, color: "var(--pg-fg)" }}
                    >
                      {section.quote.title}
                    </h3>
                    <p className="mt-3 text-[16px] leading-[1.7] whitespace-pre-line" style={{ color: "var(--pg-fg)" }}>
                      {withEmphasis(section.quote.content)}
                    </p>
                  </blockquote>
                ) : null}
              </section>
            </Reveal>
          ))}

        </article>

        <Reveal staticBelowMd>
          <div
            className="mt-16 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
            style={{ borderTop: "1px solid var(--pg-line)", paddingTop: "2.25rem" }}
          >
            <div>
              <h2 className="text-[17px] font-medium tracking-tight">More from {BLOG_AUTHOR.publication}</h2>
              <p className="mt-2 max-w-sm text-[14px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                Product updates, guides, and notes from the BOOTH team.
              </p>
            </div>
            <LocaleLink
              href="/blog"
              className="inline-flex items-center gap-1.5 text-[13.5px] font-medium transition-opacity hover:opacity-60"
            >
              All articles
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </LocaleLink>
          </div>
        </Reveal>
      </main>

      <PublicFooter />
    </GlassSurface>
  )
}
